import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import {
  CLINIC_TIME_ZONE,
  addClinicDays,
  clinicDateAt,
  clinicDateStringFromInstant,
  clinicDayIndex,
  clinicTodayDateString,
} from "@/lib/clinicTime";

// =====================================================================
// MiniMonthPicker — small left-rail month grid for the Calendar.
// Click a day → onChange(Date). "Today" highlighted in teal; selected
// day highlighted in slate-900. Days with at least one non-cancelled
// appointment in the visible month get a small teal dot under the
// number so the receptionist can see which days are busy at a
// glance without scrolling through the day-grid one date at a time.
// =====================================================================
export default function MiniMonthPicker({ value, onChange, refreshKey = 0, branchId = null, enabled = true }) {
  const [view, setView] = useState(() => firstOfMonth(value || new Date()));

  const today = clinicTodayDateString();
  const selected = clinicDateStringFromInstant(value || clinicDateAt(today, 0));

  const days = useMemo(() => buildMonthGrid(view), [view]);

  // Set of YYYY-MM-DD strings (clinic-local) where at least one
  // appointment exists. Computed from list_appointments over the
  // visible month range. Refetches when `view` changes — paging
  // months triggers a fresh load. `refreshKey` lets the parent Calendar
  // request a refetch after a booking/reschedule updates appointments.
  const [busyDays, setBusyDays] = useState(() => new Set());
  useEffect(() => {
    if (!enabled || !branchId) {
      setBusyDays(new Set());
      return undefined;
    }

    let cancelled = false;
    const from = firstOfMonth(view);
    const to   = addMonths(view, 1);
    (async () => {
      const { data, error } = await supabase.rpc("list_appointments", {
        p_from: clinicDateAt(from, 0).toISOString(),
        p_to:   clinicDateAt(to, 0).toISOString(),
        p_branch_id: branchId,
      });
      if (cancelled) return;
      if (error) { setBusyDays(new Set()); return; }
      // Cancelled / No-Show / Rescheduled don't visually count as busy.
      const next = new Set();
      for (const a of (data ?? [])) {
        if (!a?.scheduled_start) continue;
        if (["Cancelled", "No Show", "Rescheduled"].includes(a.status)) continue;
        next.add(clinicDateStringFromInstant(a.scheduled_start));
      }
      setBusyDays(next);
    })();
    return () => { cancelled = true; };
  }, [branchId, enabled, view, refreshKey]);

  return (
    <div data-testid="mini-month-picker">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setView(addMonths(view, -1))}
          className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-3.5 h-3.5 text-slate-600" />
        </button>
        <p className="text-sm font-semibold text-slate-800 tabular-nums">
          {clinicDateAt(view, 12).toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: CLINIC_TIME_ZONE })}
        </p>
        <button
          type="button"
          onClick={() => setView(addMonths(view, 1))}
          className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          aria-label="Next month"
        >
          <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-y-1 text-[10px] font-semibold text-slate-400 mb-1.5">
        {["S","M","T","W","T","F","S"].map((d, i) => (
          <span key={i} className="text-center">{d}</span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-1">
        {days.map((d, i) => {
          const inMonth = d.slice(0, 7) === view.slice(0, 7);
          const isToday = d === today;
          const isSelected = d === selected;
          const isBusy = inMonth && busyDays.has(d);
          const cls = isSelected
            ? "bg-slate-900 text-white shadow-sm"
            : isToday
              ? "bg-teal-100 text-teal-900"
              : inMonth
                ? "text-slate-700 hover:bg-slate-100"
                : "text-slate-300 hover:bg-slate-50";
          // Busy dot color: white when the day is the selected (dark
          // background) so it stays legible; teal otherwise.
          const dotColor = isSelected ? "bg-white" : "bg-teal-500";
          return (
            <div key={i} className="relative w-7 h-7 mx-auto">
              <button
                type="button"
                onClick={() => onChange?.(clinicDateAt(d, 0))}
                data-testid={`mini-month-${d}`}
                aria-label={isBusy ? `${d} (has bookings)` : d}
                className={`absolute inset-0 text-xs w-full h-full rounded-lg transition-colors ${cls}`}
              >
                {Number(d.slice(8, 10))}
              </button>
              {isBusy ? (
                <span
                  aria-hidden
                  data-testid={`mini-month-busy-${d}`}
                  className={`pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-0.5 w-1 h-1 rounded-full ${dotColor}`}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function firstOfMonth(value) {
  const date = typeof value === "string" ? value : clinicDateStringFromInstant(value);
  return `${date || clinicTodayDateString()}`.slice(0, 7) + "-01";
}
function addMonths(date, n) {
  const [year, month] = String(date).split("-").map(Number);
  const zeroBased = (year * 12) + (month - 1) + Number(n || 0);
  const nextYear = Math.floor(zeroBased / 12);
  const nextMonth = (zeroBased % 12) + 1;
  return `${nextYear}-${String(nextMonth).padStart(2, "0")}-01`;
}

// 6 weeks × 7 days = 42-cell grid starting on Sunday before first-of-month
function buildMonthGrid(monthStart) {
  const firstWeekday = clinicDayIndex(monthStart) || 0;   // 0 = Sun
  const start = addClinicDays(monthStart, -firstWeekday);
  const out = [];
  for (let i = 0; i < 42; i++) {
    out.push(addClinicDays(start, i));
  }
  return out;
}
